import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { applyPageMetadata, applyStructuredData } from '../utils/seo';

const faqs = [
  {
    question: 'Is BarsConnect a mobile app or a website?',
    answer: 'BarsConnect is currently accessible as a web platform from modern desktop and mobile browsers.',
  },
  {
    question: 'Is the platform free for bartenders?',
    answer: 'Yes. Creating a profile and using BarsConnect is free for bartenders during beta, with no credit card required.',
  },
  {
    question: 'How does BarsConnect match bartenders with bars?',
    answer: 'Profile details such as skills, location, and availability help owners identify relevant bartender profiles.',
  },
  {
    question: 'Can I update my profile later?',
    answer: 'Yes. You can update profile details as your availability and preferences change.',
  },
  {
    question: 'Do I need to apply to each job listing?',
    answer: 'Your profile can be discovered by owners who are actively browsing for bartenders, and they can contact you directly through messaging.',
  },
  {
    question: 'What information is needed to sign up?',
    answer: 'Signup includes first name, last name, email address, optional phone number, password, and role selection during registration.',
  },
];

export default function BartenderAppPage() {
  useEffect(() => {
    const cleanupMeta = applyPageMetadata({
      title: 'Bartender App — Manage Your Profile and Find Work | BarsConnect',
      description: 'BarsConnect is the bartender app built for the industry. Create a free profile, show your availability, and connect with bars hiring for full-time, part-time, and event shifts.',
      keywords: 'bartender app, bartender web app, bar hiring platform, bartender profile platform',
      canonicalUrl: 'https://barsconnect.com/bartender-app',
      robots: 'index,follow',
    });

    const cleanupSchema = applyStructuredData([
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://barsconnect.com/' },
          { '@type': 'ListItem', position: 2, name: 'Bartender App', item: 'https://barsconnect.com/bartender-app' },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer,
          },
        })),
      },
    ]);

    return () => {
      cleanupMeta();
      cleanupSchema();
    };
  }, []);

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8" data-testid="bartender-app-page">
      <div className="max-w-5xl mx-auto">
        <header className="mb-10" data-testid="bartender-app-hero-section">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4" data-testid="bartender-app-title">The Bartender App Built for How the Industry Actually Works</h1>
          <p className="text-muted-foreground mb-4" data-testid="bartender-app-hero-copy-1">
            BarsConnect is a browser-based bartender platform designed around profile visibility, availability, and direct owner outreach.
          </p>
          <p className="text-muted-foreground mb-6" data-testid="bartender-app-hero-copy-2">
            Access BarsConnect from desktop or mobile browser, create your profile, and connect with opportunities that match your working style.
          </p>
          <Button asChild data-testid="bartender-app-primary-cta-btn">
            <Link to="/register/bartenders">Create Free Bartender Profile</Link>
          </Button>
        </header>

        <section className="mb-10" data-testid="bartender-app-how-it-works-section">
          <h2 className="text-2xl font-semibold text-white mb-4" data-testid="bartender-app-how-it-works-heading">How It Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-white/5 border border-white/10" data-testid="bartender-app-step-1-card">
              <CardContent className="p-5">
                <h3 className="text-white font-semibold mb-2" data-testid="bartender-app-step-1-title">Step 1 — Create Your Profile</h3>
                <p className="text-sm text-muted-foreground" data-testid="bartender-app-step-1-copy">Add experience, preferred work type, location, and availability details.</p>
              </CardContent>
            </Card>
            <Card className="bg-white/5 border border-white/10" data-testid="bartender-app-step-2-card">
              <CardContent className="p-5">
                <h3 className="text-white font-semibold mb-2" data-testid="bartender-app-step-2-title">Step 2 — Get Discovered</h3>
                <p className="text-sm text-muted-foreground" data-testid="bartender-app-step-2-copy">Bar owners can review relevant profiles based on the details bartenders provide.</p>
              </CardContent>
            </Card>
            <Card className="bg-white/5 border border-white/10" data-testid="bartender-app-step-3-card">
              <CardContent className="p-5">
                <h3 className="text-white font-semibold mb-2" data-testid="bartender-app-step-3-title">Step 3 — Manage Connections</h3>
                <p className="text-sm text-muted-foreground" data-testid="bartender-app-step-3-copy">Use platform messaging to review opportunities and continue conversations on your schedule.</p>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="mb-10" data-testid="bartender-app-content-section">
          <h2 className="text-2xl font-semibold text-white mb-3" data-testid="bartender-app-content-heading">One Profile for Multiple Work Styles</h2>
          <p className="text-muted-foreground mb-4" data-testid="bartender-app-content-copy-1">
            A single BarsConnect profile can support full-time roles, part-time schedules, and event opportunities without creating separate accounts.
          </p>
          <p className="text-muted-foreground" data-testid="bartender-app-content-copy-2">
            Explore <Link className="text-primary hover:underline" to="/bartender-jobs" data-testid="bartender-app-inline-link-general">bartender jobs</Link>,
            {' '}<Link className="text-primary hover:underline" to="/part-time-bartender-jobs" data-testid="bartender-app-inline-link-part-time">part-time roles</Link>,
            {' '}<Link className="text-primary hover:underline" to="/event-bartender-jobs" data-testid="bartender-app-inline-link-event">event work</Link>, and
            {' '}<Link className="text-primary hover:underline" to="/bartender-jobs-near-me" data-testid="bartender-app-inline-link-near-me">local opportunities</Link>.
          </p>
        </section>

        <section className="mb-10" data-testid="bartender-app-access-section">
          <h2 className="text-2xl font-semibold text-white mb-3" data-testid="bartender-app-access-heading">Access BarsConnect From Any Device</h2>
          <p className="text-muted-foreground" data-testid="bartender-app-access-copy">
            BarsConnect is currently positioned as a web-platform experience that works across desktop and mobile browsers.
          </p>
        </section>

        <section className="mb-10" data-testid="bartender-app-faq-section">
          <h2 className="text-2xl font-semibold text-white mb-4" data-testid="bartender-app-faq-heading">Frequently Asked Questions</h2>
          <div className="space-y-3">
            {faqs.map((item, index) => (
              <Card key={item.question} className="bg-white/5 border border-white/10" data-testid={`bartender-app-faq-card-${index + 1}`}>
                <CardContent className="p-5">
                  <h3 className="text-white font-semibold mb-2" data-testid={`bartender-app-faq-question-${index + 1}`}>{item.question}</h3>
                  <p className="text-sm text-muted-foreground" data-testid={`bartender-app-faq-answer-${index + 1}`}>{item.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        <section data-testid="bartender-app-repeat-cta-section">
          <Card className="bg-white/5 border border-white/10" data-testid="bartender-app-repeat-cta-card">
            <CardContent className="p-6 sm:p-8">
              <h2 className="text-2xl font-semibold text-white mb-3" data-testid="bartender-app-repeat-cta-heading">Your next opportunity starts with a complete profile.</h2>
              <p className="text-muted-foreground mb-5" data-testid="bartender-app-repeat-cta-copy">Set up your profile in minutes and stay visible to bars that are actively hiring.</p>
              <div className="flex flex-wrap gap-3">
                <Button asChild data-testid="bartender-app-repeat-cta-register-btn">
                  <Link to="/register/bartenders">Create Free Bartender Profile</Link>
                </Button>
                <Button asChild variant="outline" data-testid="bartender-app-repeat-cta-jobs-btn">
                  <Link to="/bartender-jobs">Browse Bartender Job Guides</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </section>
      </div>
    </div>
  );
}
